function appendScriptToDom(
  script: HTMLScriptElement,
  node: HTMLElement | string | null,
): void {
  if (typeof node === "string") {
    const temp: ChildNode = document.getElementsByTagName("script")[0];

    temp?.parentNode?.insertBefore(script, temp);
  } else {
    const temp: ChildNode = node ?? document.body;

    temp.appendChild(script);
  }
}

export type CreateScriptOptions = {
  /**
   * The source url for the script to load
   */
  src: string;
  /**
   * When true `async` attribute will be added. When false or undefined, `defer` will be added.
   */
  isAsyncOrDeferOtherwise?: boolean;
  /**
   * Where to inject the script tag on the page.
   * If this is an HtmlElement then it will be added to the element using `appendChild()`
   * If this is null then it will be added to document.body with `appendChild()`
   * If this is a string, then it will be inserted before the first <script /> tag on the page.
   */
  node: HTMLElement | string | null;
  /**
   * Object of attribute/value pairs that will be added to the <script> tag created using
   * setAttribute
   */
  parameters?: Record<string, string>;
  /**
   * Object of datasets to be added to the <script /> tag when created.
   */
  datasets?: Partial<DOMStringMap>;
};

const defaultCreateScriptOptions: Omit<CreateScriptOptions, "src"> = {
  isAsyncOrDeferOtherwise: true,
  node: null,
  parameters: {},
  datasets: {},
};

// Injects <script> tag into the DOM, returns the script element
function createScriptTag(options: CreateScriptOptions): HTMLScriptElement {
  const { src, isAsyncOrDeferOtherwise, node, parameters, datasets } = {
    ...defaultCreateScriptOptions,
    ...options,
  };

  const script: HTMLScriptElement = document.createElement("script");

  script.async = !!isAsyncOrDeferOtherwise;
  script.defer = !isAsyncOrDeferOtherwise;
  script.src = src;

  if (parameters) {
    Object.keys(parameters).forEach((parameter) => {
      script.setAttribute(parameter, parameters[parameter]);
    });
  }

  if (datasets) {
    Object.keys(datasets).forEach((dataset) => {
      script.dataset[dataset] = datasets[dataset];
    });
  }

  appendScriptToDom(script, node);

  return script;
}

/**
 * Injects <script> tag, loads the script and returns a promise that resolves when the script is
 * loaded.
 *
 * You can provide a single src url which will inject the script using `document.body.appendChild()`
 * and adding the `defer` attribute.
 *
 * ```ts
 * loadScript("https://example.org/custom/script.js")
 * // creates <script src="https://example.com/custom/script.js" async></script>
 * ```
 *
 * If you need to customize the location of the script, attributes, or datasets, pass in an object
 * instead. @see {@link CreateScriptOptions}.
 *
 * ```ts
 * loadScript({
 *   src: "https://example.com/custom/script.js",
 *   isAsyncOrDeferOtherwise: true, // make async
 *   node: document.getElementById("#append-after-this"),
 *   parameters: { type: "application/javascript" },
 *   datasets: { customData: "mydata", testId: "test-id" }
 * })
 * // Creates <script src="https://example.com/custom/script.js" async type="application/javascript" data-custom-data="mydata" data-test-id="test-id"></script>
 * // appendsChild on the html element with the id `append-after-this`
 * ```
 *
 *
 */
export async function loadScript(
  srcOrOptions: string | CreateScriptOptions,
): Promise<Event> {
  return new Promise((resolve, reject) => {
    const script: HTMLScriptElement = createScriptTag(
      typeof srcOrOptions === "string"
        ? {
            src: srcOrOptions,
            node: null,
          }
        : srcOrOptions,
    );

    script.onload = resolve;
    script.onerror = reject;
  });
}
